import type { AssetSource, AssetEntry, LoadedAsset, AssetNamespace } from '@gotti/core';

/**
 * The area's channel to the master (the asset store + change authority).
 * `write` relays a save/remove; `read` fetches bytes for a cold load.
 * The concrete impl (ServerProcess) marshals bytes over the JSON server wire.
 */
export interface MasterAssetTransport {
  write(op: 'save' | 'remove', payload: { namespace: AssetNamespace; path: string; bytes?: Uint8Array }): Promise<void>;
  // `read` may additionally carry decoded forms (`image`/`url`) when the backing
  // source produced them (a DOM-backed source decodes images to an
  // HTMLImageElement + object URL). Both are optional: the real server wire
  // transport (base64 ASSET_READ) never populates them, so bytes-only reads are
  // unchanged; a browser-backed in-process transport forwards them so a sync
  // getImage() after preload works.
  read(payload: { namespace: AssetNamespace; path: string }): Promise<{ bytes: Uint8Array; mimeType: string; image?: unknown; url?: string } | null>;
}

/** AssetSource that satisfies BaseAssetManager's cold-load by asking the master. */
export class MasterReadSource implements AssetSource {
  constructor(private readonly transport: MasterAssetTransport) {}
  manifest(): AssetEntry[] { return []; }
  async load(fullPath: string): Promise<LoadedAsset> {
    const idx = fullPath.indexOf('/');
    const namespace = fullPath.slice(0, idx) as AssetNamespace;
    const path = fullPath.slice(idx + 1);
    const r = await this.transport.read({ namespace, path });
    if (!r) throw new Error(`MasterReadSource: no asset at "${fullPath}".`);
    // Forward any decoded forms the transport carried so the cached asset stays
    // usable via getImage()/getUrl(); the wire transport omits them (bytes-only).
    const out: LoadedAsset = { path: fullPath, mimeType: r.mimeType, bytes: r.bytes };
    if (r.image !== undefined) out.image = r.image;
    if (r.url !== undefined) out.url = r.url;
    return out;
  }
}
